'use client';

import { useState, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import Icon from '@/components/ui/AppIcon';

interface MapLocation {
  lat: number;
  lng: number;
  label: string;
  type: 'pickup' | 'transit' | 'delivery' | 'current';
  completed: boolean;
}

interface LiveMapTrackingProps {
  locations: MapLocation[];
  currentLocation?: MapLocation;
}

const mapContainerStyle = {
  width: '100%',
  height: '500px',
};

const defaultCenter = {
  lat: 40.7128,
  lng: -74.0060,
};

export default function LiveMapTracking({ locations, currentLocation }: LiveMapTrackingProps) {
  const [selectedMarker, setSelectedMarker] = useState<MapLocation | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

  const getMarkerIcon = (type: string, completed: boolean) => {
    if (type === 'current') {
      return {
        path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
        scale: 10,
        fillColor: '#3b82f6',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      };
    }
    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
      scale: 8,
      fillColor: completed ? '#10b981' : '#94a3b8',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    };
  };

  const polylineOptions = {
    strokeColor: '#3b82f6',
    strokeOpacity: 0.8,
    strokeWeight: 3,
  };

  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
    return (
      <div className="bg-card rounded-xl p-8 shadow-card border border-border">
        <div className="text-center">
          <Icon name="MapIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Google Maps API Key Required</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Please add your Google Maps API key to the .env file:
          </p>
          <code className="bg-muted px-4 py-2 rounded text-sm text-foreground">
            NEXT_PUBLIC_GOOGLE_MAPS_API_KEY=your-api-key
          </code>
        </div>
      </div>
    );
  }

  const center = currentLocation
    ? { lat: currentLocation.lat, lng: currentLocation.lng }
    : locations.length > 0
    ? { lat: locations[0].lat, lng: locations[0].lng }
    : defaultCenter;

  const routePath = locations.map((loc) => ({ lat: loc.lat, lng: loc.lng }));

  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="MapIcon" size={24} className="text-primary" />
          Live Tracking Map
        </h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-3 h-3 rounded-full bg-success"></div>
          <span>Completed</span>
          <div className="w-3 h-3 rounded-full bg-muted-foreground ml-2"></div>
          <span>Pending</span>
          <div className="w-3 h-3 rounded-full bg-primary ml-2"></div>
          <span>Current</span>
        </div>
      </div>

      <div className="rounded-lg overflow-hidden border border-border">
        <LoadScript googleMapsApiKey={apiKey} onLoad={onMapLoad}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={4}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
            }}
          >
            {/* Route Polyline */}
            {mapLoaded && routePath.length > 1 && (
              <Polyline path={routePath} options={polylineOptions} />
            )}

            {/* Location Markers */}
            {mapLoaded &&
              locations.map((location, index) => (
                <Marker
                  key={index}
                  position={{ lat: location.lat, lng: location.lng }}
                  icon={getMarkerIcon(location.type, location.completed)}
                  onClick={() => setSelectedMarker(location)}
                />
              ))}

            {/* Current Location Marker */}
            {mapLoaded && currentLocation && (
              <Marker
                position={{ lat: currentLocation.lat, lng: currentLocation.lng }}
                icon={getMarkerIcon('current', true)}
                onClick={() => setSelectedMarker(currentLocation)}
                animation={window.google?.maps?.Animation?.BOUNCE}
              />
            )}

            {/* Info Window */}
            {selectedMarker && (
              <InfoWindow
                position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="p-2">
                  <h4 className="font-semibold text-sm mb-1">{selectedMarker.label}</h4>
                  <p className="text-xs text-gray-600">
                    Status: {selectedMarker.completed ? 'Completed' : 'Pending'}
                  </p>
                  {selectedMarker.type === 'current' && (
                    <p className="text-xs text-blue-600 font-medium mt-1">Current Location</p>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-muted rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Total Stops</p>
          <p className="text-lg font-semibold text-foreground">{locations.length}</p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Completed</p>
          <p className="text-lg font-semibold text-success">
            {locations.filter((loc) => loc.completed).length}
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Remaining</p>
          <p className="text-lg font-semibold text-warning">
            {locations.filter((loc) => !loc.completed).length}
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Progress</p>
          <p className="text-lg font-semibold text-primary">
            {Math.round((locations.filter((loc) => loc.completed).length / locations.length) * 100)}%
          </p>
        </div>
      </div>
    </div>
  );
}
